import React, { ReactNode, useState, useEffect } from "react";
import Header from "@cloudscape-design/components/header";
import { TyreMasterTable } from "../data";
import Table, { TableProps } from "@cloudscape-design/components/table";
import { useCollection } from "@cloudscape-design/collection-hooks";
import {
  Checkbox,
  CollectionPreferences,
  CollectionPreferencesProps,
  Container,
  FormField,
  Input,
  SpaceBetween,
  StatusIndicator,
  Flashbar,
} from "@cloudscape-design/components";
import Select from "@cloudscape-design/components/select";
import Pagination from "@cloudscape-design/components/pagination";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import TextFilter from "@cloudscape-design/components/text-filter";
import title from "process";
import Modal from "@cloudscape-design/components/modal";
import { Link } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import {
  createTyre,
  deleteTyre,
  updateTyre,
} from "../../../../graphql/mutations";
import { fetchAuthSession } from "aws-amplify/auth";

// get user belong to what group and make it into string
async function checkUserGroup() {
  try {
    const { tokens } = await fetchAuthSession();
    // Normalize user groups to be an array of strings
    const groups = tokens?.accessToken.payload["cognito:groups"];
    if (Array.isArray(groups)) {
      return groups as string[]; // Safe to cast as string[]
    } else if (typeof groups === "string") {
      return [groups]; // Convert a single string to an array
    } else {
      return []; // Default to an empty array if not an array or string
    }
  } catch (error) {
    console.error("Error fetching user groups:", error);
    return []; // Default to an empty array in case of an error
  }
}

async function checkCurrentUser(): Promise<{
  username: string;
  email: string | null;
}> {
  try {
    const { tokens } = await fetchAuthSession();

    // Extract the user's information from the accessToken or idToken
    const currentUser =
      tokens?.idToken?.payload || tokens?.accessToken?.payload;

    if (currentUser) {
      const username =
        typeof currentUser["cognito:username"] === "string"
          ? currentUser["cognito:username"]
          : ""; // Fallback to an empty string if username is not a string

      // Check if email is a string, otherwise default to null
      const email =
        typeof currentUser.email === "string" ? currentUser.email : null; // Fallback to null if email is not a string

      return { username, email }; // Return user info with valid types
    } else {
      console.error("No user information found.");
      return { username: "", email: null }; // Return empty values if no user data
    }
  } catch (error) {
    console.error("Error fetching current user information:", error);
    return { username: "", email: null }; // Default to empty values in case of error
  }
}

interface FlashbarItem {
  type: "info" | "warning" | "error" | "success"; // Adjust according to your requirements
  dismissible: boolean;
  dismissLabel: string;
  onDismiss: () => void;
  content: React.ReactNode;
  id: string;
}

const client = generateClient();
async function createTyreApi(
  serialnumber: string,
  brand: string,
  typetyre: string,
  vehiclecode: string,
  position: number,
  sensorid: number,
  pressure: number,
  temperature: number,
  battery: number,
  status: boolean
) {
  try {
    await client.graphql({
      query: createTyre,
      authMode: "userPool",
      variables: {
        input: {
          serialnumber: serialnumber,
          brand: brand,
          typetyre: typetyre,
          vehiclecode: vehiclecode,
          position: position,
          sensorid: sensorid,
          pressure: pressure,
          temperature: temperature,
          battery: battery,
          status: status,
        },
      },
    });
  } catch (err) {
    console.log(err);
  }
}

async function updateTyreApi(
  id: string,
  serialnumber: string,
  brand: string,
  typetyre: string,
  vehiclecode: string,
  position: number,
  sensorid: number,
  pressure: number,
  temperature: number,
  battery: number,
  status: boolean
) {
  try {
    await client.graphql({
      query: updateTyre,
      authMode: "userPool",
      variables: {
        input: {
          id: id,
          serialnumber: serialnumber,
          brand: brand,
          typetyre: typetyre,
          vehiclecode: vehiclecode,
          position: position,
          sensorid: sensorid,
          pressure: pressure,
          temperature: temperature,
          battery: battery,
          status: status,
        },
      },
    });
  } catch (err) {
    console.log("Error updating tyre: ", err);
  }
}

async function deleteTyreApi(id: string) {
  try {
    const deletedTyre = await client.graphql({
      query: deleteTyre,
      authMode: "userPool",
      variables: {
        input: {
          id,
        },
      },
    });
    // Optionally, refresh the list after deletion
    // fetchOverview();
  } catch (err) {
    console.log("Error deleting tyre : ", err);
  }
}

interface CellProps {
  Status: boolean;
}

const getFilterCounterText = (count: number = 0) =>
  `${count} ${count === 1 ? "match" : "matches"}`;

export interface VariationTableProps {
  TyreMaster: TyreMasterTable[];
  refreshData: () => void;
}
const TyreStatus: React.FC<CellProps> = ({ Status }) => {
  let statusType: "success" | "error" = "error"; // status types (status indicator cloudscape)

  // Conditional rendering logic
  if (Status === true) {
    statusType = "success";
  } else if (Status === false) {
    statusType = "error";
  }

  return (
    <StatusIndicator type={statusType}>
      {statusType === "success"
        ? "Plug"
        : statusType === "error"
        ? "Unplug"
        : "Unknown Status"}
    </StatusIndicator>
  );
};

const columnDefinitions: TableProps<TyreMasterTable>["columnDefinitions"] = [
  {
    header: "ID",
    cell: ({ id }) => id,
    sortingField: "id",
    minWidth: 10,
  },

  {
    header: "S/N",
    cell: ({ sn }) => <Link to={`/vehicles/tyres/${sn}`}>{sn}</Link>,
    sortingField: "sn",
    minWidth: 100,
  },

  {
    header: "Brand",
    cell: ({ brand }) => brand,
    sortingField: "brand",
    minWidth: 100,
  },
  {
    header: "Tyre Type",
    cell: ({ typetyre }) => typetyre,
    sortingField: "typetyre",
    minWidth: 100,
  },
  {
    header: "Vehicle Code",
    cell: ({ VehicleCode }) => VehicleCode,

    /*(
    <Link to={`/Vehicle-Detail/${VehicleCode}`}>
      {VehicleCode}
    </Link>
  )*/
    sortingField: "VehicleCode",
    minWidth: 100,
  },
  {
    header: "Position",
    cell: ({ tyreId }) => tyreId,
    sortingField: "tyreId",
    minWidth: "10%",
  },
  {
    header: "Sensor ID",
    cell: ({ sensorId }) => sensorId,
    sortingField: "sensorId",
    minWidth: 100,
  },
  {
    header: "Pressure",
    cell: ({ pressure }) => pressure,
    sortingField: "Pressure",
    minWidth: 100,
  },
  {
    header: "Temperature",
    cell: ({ temperature }) => temperature,
    sortingField: "temperature",
    minWidth: 100,
  },
  {
    header: "Battery ",
    cell: ({ battery }) => battery,
    sortingField: "battery",
    minWidth: 100,
  },
  {
    header: "Status ",
    cell: TyreStatus,
    sortingField: "Status",
    minWidth: 100,
  },
];

const EmptyState = ({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action: ReactNode;
}) => {
  return (
    <Box textAlign='center' color='inherit'>
      <Box variant='strong' textAlign='center' color='inherit'>
        {title}
      </Box>
      <Box variant='p' padding={{ bottom: "s" }} color='inherit'>
        {subtitle}
      </Box>
      {action}
    </Box>
  );
};

const getHeaderCounterText = (
  items: readonly TyreMasterTable[] = [],
  selectedItems: readonly TyreMasterTable[] = [],
  displayedItemsCount: number = 0
) => {
  return selectedItems && selectedItems.length > 0
    ? `(${selectedItems.length}/${displayedItemsCount}/${items.length})`
    : `(${displayedItemsCount}/${items.length})`;
};

export default function VariationTable({
  TyreMaster,
  refreshData,
}: VariationTableProps) {
  const [preferences, setPreferences] = useState<
    CollectionPreferencesProps["preferences"]
  >({ pageSize: 10 });
  const [visible, setVisible] = useState(false);
  const [visible2, setVisible2] = useState(false);
  const [checked, setChecked] = useState(false);
  //const handleCancelClick = () => { setVisible(false) };
  const [isModalDeleteVisible, setModalDeleteVisible] = useState(false);
  const [tyreSN, settyreSN] = useState("");
  const [tyreBrand, settyreBrand] = useState("");
  const [tyreTipe, settyreTipe] = useState("");
  const [tyreVehicleCode, settyreVehicleCode] = useState("");
  const [tyrePosition, settyrePosition] = useState(0);
  const [tyreSensorId, settyreSensorId] = useState(0);
  const [tyrePressure, settyrePressure] = useState(0);
  const [tyreTemperature, settyreTemperature] = useState(0);
  const [tyreBattery, settyreBattery] = useState(0);
  const [tyreStatus, settyreStatus] = useState<boolean | false>(false);
  const [selectedTyreId, setSelectedTyreId] = useState<string | null>(null);
  const [comboVehicleCode, setcomboVehicleCode] = useState({ value: "1" });
  const [itemsFlashbar, setItemsFlashbar] = useState<FlashbarItem[]>([]);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [currentUser, setCurrentUser] = useState<string>("");
  const [currentUserEmail, setCurrentUserEmail] = useState<string>("");
  const timestamp = new Date().toISOString();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync checkbox state with tyreStatus
  useEffect(() => {
    // const fetchedTyreStatus = tyreStatus;
    async function initialize() {
      const groups = await checkUserGroup();
      const user = await checkCurrentUser();
      setCurrentUser(user.username);
      setCurrentUserEmail(user.email ?? "");
      setUserGroups(groups);
      setChecked(tyreStatus === true);
    }
    initialize();
  }, [tyreStatus]);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const status = tyreStatus !== null ? tyreStatus : false;

      if (selectedTyreId) {
        await updateTyreApi(
          selectedTyreId,
          tyreSN,
          tyreBrand,
          tyreTipe,
          tyreVehicleCode,
          tyrePosition,
          tyreSensorId,
          tyrePressure,
          tyreTemperature,
          tyreBattery,
          status
        );
        // Log Transaction of update tyre
        console.log(
          `Tyre Transaction Log (Update Item):
        Tyre ID : ${selectedTyreId}
        Tyre SN : ${tyreSN}
        Tyre Brand : ${tyreBrand}
        Tyre Tipe : ${tyreTipe}
        Tyre Vehicle Code : ${tyreVehicleCode}
        Tyre Position : ${tyrePosition}
        Tyre Sensor ID : ${tyreSensorId}
        Tyre Status : ${tyreStatus}
        User Group: ${userGroups}
        User : ${currentUser}
        User Email : ${currentUserEmail}
        Timestamp : ${timestamp}`
        );
        setItemsFlashbar([
          {
            type: "success",
            dismissible: true,
            dismissLabel: "Dismiss message",
            onDismiss: () => setItemsFlashbar([]),
            content: "Tyre successfully updated.",
            id: "message_3",
          },
        ]);
      } else {
        await createTyreApi(
          tyreSN,
          tyreBrand,
          tyreTipe,
          tyreVehicleCode,
          tyrePosition,
          tyreSensorId,
          tyrePressure,
          tyreTemperature,
          tyreBattery,
          status
        );
        // Log Transaction of create tyre
        console.log(
          `Tyre Transaction Log (Add/Create Item):
        Tyre ID : ${selectedTyreId}
        Tyre SN : ${tyreSN}
        Tyre Brand : ${tyreBrand}
        Tyre Tipe : ${tyreTipe}
        Tyre Vehicle Code : ${tyreVehicleCode}
        Tyre Position : ${tyrePosition}
        Tyre Sensor ID : ${tyreSensorId}
        Tyre Status : ${tyreStatus}
        User Group: ${userGroups}
        User : ${currentUser}
        User Email : ${currentUserEmail}
        Timestamp : ${timestamp}`
        );

        setItemsFlashbar([
          {
            type: "success",
            dismissible: true,
            dismissLabel: "Dismiss message",
            onDismiss: () => setItemsFlashbar([]),
            content: "Tyre successfully created.",
            id: "message_2",
          },
        ]);
      }
      refreshData();
      setVisible(false);
      setVisible2(false);
      clearFormState();
    } catch (error) {
      console.error("Error updateing/creating tyre:", error);
      setItemsFlashbar([
        {
          type: "error",
          dismissible: true,
          dismissLabel: "Dismiss message",
          onDismiss: () => setItemsFlashbar([]),
          content: "An error occurred. Please try again.",
          id: "message_error",
        },
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const selectedItem = collectionProps.selectedItems?.[0];
    if (selectedItem) {
      await deleteTyreApi(selectedItem.id);
      setModalDeleteVisible(false);
      // Log Transaction of delete tyre
      console.log(
        `Tyre Transaction Log (Delete Item):
        Tyre ID : ${selectedItem.id}
        Tyre SN : ${selectedItem.sn}
        Tyre Brand : ${selectedItem.brand}
        Tyre Tipe : ${selectedItem.typetyre}
        Tyre Vehicle Code : ${selectedItem.VehicleCode}
        Tyre Position : ${selectedItem.tyreId}
        Tyre Sensor ID : ${selectedItem.sensorId}
        Tyre Status : ${selectedItem.Status}
        User Group: ${userGroups}
        User : ${currentUser}
        User Email : ${currentUserEmail}
        Timestamp : ${timestamp}`
      );

      refreshData();
      setItemsFlashbar([
        {
          type: "warning",
          dismissible: true,
          dismissLabel: "Dismiss message",
          onDismiss: () => setItemsFlashbar([]),
          content: "Tyre successfully deleted !",
          id: "message_1",
        },
      ]);
    }
  };

  const handleDeleteCOnfirmation = () => {
    setModalDeleteVisible(true);
  };

  // Clear form state
  const clearFormState = () => {
    settyreSN("");
    settyreBrand("");
    settyreTipe("");
    settyreVehicleCode("");
    settyrePosition(0);
    settyreSensorId(0);
    settyrePressure(0);
    settyreTemperature(0);
    settyreBattery(0);
    settyreStatus(false);
    setChecked(false);
    setSelectedTyreId(null);
    setcomboVehicleCode({ value: "1" });
  };

  const {
    items,
    filterProps,
    actions,
    filteredItemsCount,
    paginationProps,
    collectionProps,
  } = useCollection<TyreMasterTable>(TyreMaster, {
    filtering: {
      filteringFunction: (item, filteringText) => {
        const filterTextLower = filteringText.toLowerCase();
        const statusText = item.Status ? "plug" : "unplug";
        if (statusText === filterTextLower) {
          return true;
        }

        return (
          item.tyreId.toString().includes(filterTextLower) ||
          item.sn.toLowerCase().includes(filterTextLower) ||
          item.sensorId.toString().includes(filterTextLower) ||
          item.brand.toLowerCase().includes(filterTextLower) ||
          item.typetyre.toLowerCase().includes(filterTextLower) ||
          item.VehicleCode.toLowerCase().includes(filterTextLower) ||
          item.pressure.toString().includes(filterTextLower) ||
          item.temperature.toString().includes(filterTextLower) ||
          item.battery.toString().includes(filterTextLower) ||
          item.id.toString().includes(filterTextLower)
        );
      },

      noMatch: (
        <EmptyState
          title='No matches'
          subtitle='We can’t find a match.'
          action={
            <Button onClick={() => actions.setFiltering("")}>
              Clear filter
            </Button>
          }
        />
      ),
    },
    pagination: { pageSize: preferences?.pageSize },
    sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
    selection: {},
  });

  const isButtonDisabled =
    !userGroups.includes("super_admin") && !userGroups.includes("tenant_admin");
  const isUpdateButtonDisabled =
    !userGroups.includes("super_admin") &&
    !userGroups.includes("tenant_admin") &&
    !userGroups.includes("operator");
  return (
    <>
      <Modal
        onDismiss={() => setModalDeleteVisible(false)}
        visible={isModalDeleteVisible}
        closeAriaLabel='Close modal'
        header='Confirm Delete'
        footer={
          <SpaceBetween direction='horizontal' size='s'>
            <Button variant='link' onClick={() => setModalDeleteVisible(false)}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleDelete}>
              Confirm
            </Button>
          </SpaceBetween>
        }
      >
        <p>Are you sure you want to delete this Tyre ?</p>
      </Modal>
      <Modal
        onDismiss={() => {
          setVisible(false);
          clearFormState();
        }}
        visible={visible}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => {
                  setVisible(false);
                  clearFormState(); // Tambahkan ini juga di sini
                }}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  !tyreBrand ||
                  !tyreTipe ||
                  !tyreSN ||
                  !tyrePosition ||
                  !tyreSensorId ||
                  !tyreVehicleCode ||
                  !tyrePressure ||
                  !tyreTemperature ||
                  !tyreBattery
                }
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </SpaceBetween>
          </Box>
        }
        header='Add Tyre'
      >
        <Container>
          <SpaceBetween direction='vertical' size='l'>
            <FormField label='Brand'>
              <Input
                value={tyreBrand}
                onChange={({ detail }) => settyreBrand(detail.value)}
              />
            </FormField>
            <FormField label='Tyre Type'>
              <Input
                value={tyreTipe}
                onChange={({ detail }) => settyreTipe(detail.value)}
              />
            </FormField>
            <FormField label='Serial Number'>
              <Input
                value={tyreSN}
                onChange={({ detail }) => settyreSN(detail.value)}
              />
            </FormField>
            <FormField label='Tyre Position'>
              <Input
                value={tyrePosition.toString()}
                onChange={({ detail }) =>
                  settyrePosition(parseInt(detail.value, 0))
                }
              />
            </FormField>
            <FormField label='Sensor ID'>
              <Input
                value={tyreSensorId.toString()}
                onChange={({ detail }) =>
                  settyreSensorId(parseInt(detail.value, 0))
                }
              />
            </FormField>

            <FormField label='Vehicle Code'>
              <Input
                value={tyreVehicleCode}
                onChange={({ detail }) => settyreVehicleCode(detail.value)}
              />
            </FormField>

            <FormField label='Pressure'>
              <Input
                value={tyrePressure.toString()}
                onChange={({ detail }) =>
                  settyrePressure(parseInt(detail.value, 0))
                }
              />
            </FormField>
            <FormField label='Temperature'>
              <Input
                value={tyreTemperature.toString()}
                onChange={({ detail }) =>
                  settyreTemperature(parseInt(detail.value, 0))
                }
              />
            </FormField>
            <FormField label='Battery'>
              <Input
                value={tyreBattery.toString()}
                onChange={({ detail }) =>
                  settyreBattery(parseInt(detail.value, 0))
                }
              />
            </FormField>
            <FormField label='Status Tyre'>
              <Checkbox
                onChange={({ detail }) => {
                  setChecked(detail.checked);
                  settyreStatus(detail.checked ? true : false); // Ensure tyreStatus is boolean
                }}
                checked={checked}
              >
                Plug
              </Checkbox>
            </FormField>
          </SpaceBetween>
        </Container>
      </Modal>

      <Modal
        onDismiss={() => {
          setVisible2(false);
          clearFormState();
        }}
        visible={visible2}
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => {
                  setVisible2(false);
                  clearFormState(); // Tambahkan ini juga di sini
                }}
              >
                Cancel
              </Button>
              <Button variant='primary' onClick={handleSubmit}>
                Submit
              </Button>
            </SpaceBetween>
          </Box>
        }
        header='Edit Tyre'
      >
        <Container>
          <SpaceBetween direction='vertical' size='l'>
            <FormField label='Brand'>
              <Input
                value={tyreBrand}
                onChange={({ detail }) => settyreBrand(detail.value)}
              />
            </FormField>
            <FormField label='Type'>
              <Input
                value={tyreTipe}
                onChange={({ detail }) => settyreTipe(detail.value)}
              />
            </FormField>
            <FormField label='Serial Number'>
              <Input
                value={tyreSN}
                onChange={({ detail }) => settyreSN(detail.value)}
              />
            </FormField>
            <FormField label='Tyre Position'>
              <Input
                value={tyrePosition.toString()}
                onChange={({ detail }) =>
                  settyrePosition(parseInt(detail.value, 0))
                }
              />
            </FormField>
            <FormField label='Sensor ID'>
              <Input
                value={tyreSensorId.toString()}
                onChange={({ detail }) =>
                  settyreSensorId(parseInt(detail.value, 0))
                }
              />
            </FormField>

            {/*<FormField label="Vehicle Code">
              <Select
                options={[
                  { label: "CO120", value: "1" },
                  { label: "CO121", value: "2" },
                  { label: "CO122", value: "3" },
                  { label: "CO123", value: "4" },
                  { label: "CO124", value: "5" },
                ]}
                selectedOption={null}
              />
            </FormField>*/}
            <FormField label='Vehicle Code'>
              <Input
                value={tyreVehicleCode}
                onChange={({ detail }) => settyreVehicleCode(detail.value)}
              />
            </FormField>

            <FormField label='Pressure'>
              <Input
                value={tyrePressure.toString()}
                onChange={({ detail }) =>
                  settyrePressure(parseInt(detail.value, 0))
                }
              />
            </FormField>
            <FormField label='Temperature'>
              <Input
                value={tyreTemperature.toString()}
                onChange={({ detail }) =>
                  settyreTemperature(parseInt(detail.value, 0))
                }
              />
            </FormField>
            <FormField label='Battery'>
              <Input
                value={tyreBattery.toString()}
                onChange={({ detail }) =>
                  settyreBattery(parseInt(detail.value, 0))
                }
              />
            </FormField>

            <Checkbox
              onChange={({ detail }) => {
                setChecked(detail.checked);
                settyreStatus(detail.checked ? true : false); // Ensure tyreStatus is boolean
              }}
              checked={checked}
            >
              Plug
            </Checkbox>
          </SpaceBetween>
        </Container>
      </Modal>
      <Flashbar items={itemsFlashbar} />
      <Table<TyreMasterTable>
        {...collectionProps}
        items={items}
        columnDefinitions={columnDefinitions}
        stickyHeader={true}
        resizableColumns={true}
        variant='container'
        ariaLabels={{
          selectionGroupLabel: "Items selection",
          itemSelectionLabel: ({ selectedItems }, item) => {
            const isItemSelected = selectedItems.filter(
              (i) => i.id === item.id
            ).length;
            return `${item.id} is ${isItemSelected ? "" : "not "}selected`;
          },
          tableLabel: "Master Vehicle table",
        }}
        header={
          <Header
            variant='h1'
            counter={getHeaderCounterText(
              TyreMaster,
              collectionProps.selectedItems ?? [],
              items.length
            )}
            actions={
              <SpaceBetween size='s' direction='horizontal'>
                <Button
                  disabled={
                    (collectionProps.selectedItems ?? []).length === 0 ||
                    isButtonDisabled
                  }
                  onClick={handleDeleteCOnfirmation}
                >
                  Delete
                </Button>

                <Button
                  disabled={
                    (collectionProps.selectedItems ?? []).length === 0 ||
                    isUpdateButtonDisabled
                  }
                  onClick={() => {
                    const selectedItem = collectionProps.selectedItems?.[0];
                    if (selectedItem) {
                      settyreSN(selectedItem.sn);
                      settyreBrand(selectedItem.brand);
                      settyreTipe(selectedItem.typetyre);
                      settyreVehicleCode(selectedItem.VehicleCode);
                      settyrePosition(selectedItem.tyreId);
                      settyreSensorId(selectedItem.sensorId);
                      settyrePressure(selectedItem.pressure);
                      settyreTemperature(selectedItem.temperature);
                      settyreBattery(selectedItem.battery);
                      settyreStatus(selectedItem.Status ? true : false);
                      setSelectedTyreId(selectedItem.id);
                      setVisible2(true);
                    }
                  }}
                  variant='primary'
                >
                  Update
                </Button>
                <Button
                  onClick={() => setVisible(true)}
                  disabled={isButtonDisabled}
                  variant='primary'
                >
                  Add Tyre
                </Button>
              </SpaceBetween>
            }
          >
            Tyres
          </Header>
        }
        pagination={<Pagination {...paginationProps} />}
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder='Find Tyre'
            countText={getFilterCounterText(filteredItemsCount)}
          />
        }
        preferences={
          <CollectionPreferences
            preferences={preferences}
            pageSizePreference={{
              title: "select page size",
              options: [
                { value: 10, label: "10 resources" },
                { value: 20, label: "20 resources" },
                { value: 50, label: "50 resources" },
                { value: 100, label: "100 resources" },
              ],
            }}
            onConfirm={({ detail }) => setPreferences(detail)}
            title='preference'
            confirmLabel='Confrim'
            cancelLabel='Cancel'
          />
        }
        selectionType='single'
      />
    </>
  );
}
